import * as React from 'react';
import _ from 'lodash';
import cn from '~modules/cn';

import './text-area.scss';

type TextAreaProps = {
    resize?: 'none' | 'vertical' | 'horizontal' | 'both';
    autoSize?: boolean;
    setRef?: React.MutableRefObject<HTMLTextAreaElement>;
    state?: 'active';
};

type Props = Omit<React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>, 'ref'> & TextAreaProps;

class TextArea extends React.Component<Props> {
    resizeObserver?: ResizeObserver;

    textArea = React.createRef<HTMLTextAreaElement>() as React.MutableRefObject<HTMLTextAreaElement>;

    updateHeightDebounced = _.debounce(this.updateHeight, 100, { maxWait: 100 });

    componentDidMount(): void {
        if (this.props.autoSize) {
            this.resizeObserver = new ResizeObserver(this.onResize);
            const textArea = this.textArea.current!;
            textArea.rows = 1;
            textArea.style.overflowY = 'hidden';
            this.updateHeight();
            this.resizeObserver.observe(textArea);
        }
    }

    componentDidUpdate() {
        this.updateHeight();
    }

    componentWillUnmount(): void {
        this.resizeObserver?.disconnect();
        this.updateHeightDebounced.cancel();
    }

    updateRef = (el: HTMLTextAreaElement): void => {
        if (this.props.setRef) {
            this.props.setRef.current = el;
        }
        this.textArea.current = el;
    };

    updateHeight() {
        const textArea = this.textArea.current!;
        textArea.style.height = 'auto';
        textArea.style.height = `${textArea.scrollHeight}px`;
    }

    onResize = (): void => {
        this.updateHeightDebounced();
    };

    onInput = (event: React.FormEvent<HTMLTextAreaElement>): void => {
        if (this.props.autoSize) {
            this.updateHeight();
        }
        this.props.onInput?.(event);
    };

    render(): JSX.Element {
        const { resize = 'none', state } = this.props;
        const rest = _.omit(this.props, ['resize', 'setRef', 'autoSize', 'state']);
        return (
            <textarea
                {...rest}
                onInput={this.onInput}
                ref={this.updateRef}
                className={cn('text-area', `text-area--resize-${resize}`, this.props.className, {
                    [`text-area--${state}`]: state
                })}
            />
        );
    }
}

export default React.forwardRef<HTMLTextAreaElement, Props>(
    (props: Props, ref): JSX.Element => <TextArea {...props} setRef={ref as React.MutableRefObject<HTMLTextAreaElement>} />
);
